import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  USER_ROLE_APARTMENT_LOCATOR,
} from 'common/userRoles';

import H1 from 'client/components/H1';
import Navbar from 'client/components/Navbar';
import ButtonDeprecated, { BUTTON_THEME_RED } from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';
import SrLink from 'client/components/SrLink/SrLink';

import {
  SR_API_ACTION_APARTMENT_LOCATOR_UPDATE_LEAD_STATUS,
} from 'api/actionTypes';
import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants';
import { SCREEN_ID_APARTMENT_LOCATOR_ARCHIVE_LEAD } from 'client/constants/screenIds';
import { actionDataTableLoadItems } from 'client/helpers/dataTables';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import {
  APARTMENT_LOCATOR_ARCHIVE_LEAD_REASON_OPTION_ITEMS,
  APARTMENT_LOCATOR_ARCHIVE_LEAD_REASON_OTHER,
  LEAD_STATUS_ARCHIVED,
} from 'common/leadConstants';
import branch from 'client/core/branch';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

function getDataTableSettingsFromProps({ match, currentUser }) {
  return {
    viewId: `leads-id-${match.params.renterUserId}`,
    filter: {
      apartmentLocatorUserId: currentUser.id,
      renterUserId: parseInt(match.params.renterUserId, 10),
    },
    count: 1,
  };
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  archiveLeadApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_UPDATE_LEAD_STATUS,
  },
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_ARCHIVE_LEAD)
@branch({
  currentUser: ['currentUser'],
  leadUser: ['apartmentLocator', 'lead', 'user'],
  leadStatus: ['apartmentLocator', 'lead', 'status'],
})
class ArchiveLeadPage extends Component {
  static fetchData(match, inject) {
    return ApartmentLocatorActions.getLead(inject, parseInt(match.params.renterUserId, 10));
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    leadUser: PropTypes.object,
    leadStatus: PropTypes.string,
  }

  static getPageMetadata = () => ({
    pageTitle: 'Archive Lead | Sunroom Rentals',
    section: 'Archive Lead',
  });

  _onSumbitArchiveReason = (values) => {
    const {
      dispatch,
      currentUser,
      leadUser,
      leadStatus,
      history,
    } = this.props;

    dispatch(ApartmentLocatorActions.updateLeadStatus, {
      apartmentLocatorUserId: currentUser.id,
      renterUserId: leadUser.id,
      toLeadStatus: LEAD_STATUS_ARCHIVED,
      entityTransitionUpdates: {
        archiveReason: values.archiveReason,
        archiveReasonUserProvided: values.reasonProvided,
      },
    }).then(() => {
      dispatch(actionDataTableLoadItems, {
        dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
        ...getDataTableSettingsFromProps(this.props),
      });
      return history.push(`/agent/leads/${leadStatus}`);
    });
  }

  render() {
    const {
      archiveLeadApiState,
      currentUser,
      leadUser,
      match: {
        params: {
          renterUserId,
        },
      },
    } = this.props;

    return (
      <div className='page-agentDashboard lead hide'>
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={
            <SrLink
              scrollToLastPosition
              redirectToLastLocation
              className='back link'
              to={`/agent/lead/${renterUserId}`}
            />
          }
        />

        <div className='contentContainer'>
          {leadUser && (
            <Formik
              enableReinitialize
              initialValues={{
                archiveReason: '',
                reasonProvided: '',
              }}
              onSubmit={this._onSumbitArchiveReason}
              render={(formikBag) => {
                const formFields = [
                  {
                    id: 'archiveReason',
                    type: 'multiple_choice',
                    properties: {
                      choices: APARTMENT_LOCATOR_ARCHIVE_LEAD_REASON_OPTION_ITEMS.map((reason) => ({
                        value: reason.value,
                        label: reason.label,
                      })),
                      multipleSelection: false,
                      allowEmptyArray: true,
                      onChange: (newValue) => {
                        formikBag.setFieldValue('archiveReason', newValue);
                        formikBag.setFieldValue('reasonProvided', '');
                      },
                    },
                  },
                  {
                    id: 'reasonProvided',
                    type: 'short_text',
                    properties: {
                      className: 'otherReason',
                      placeholder: 'Other...',
                      onChange: (event) => {
                        formikBag.setFieldValue(
                          'archiveReason',
                          APARTMENT_LOCATOR_ARCHIVE_LEAD_REASON_OTHER
                        );
                        formikBag.setFieldValue('reasonProvided', event.target.value);
                      },
                    },
                  },
                ];

                return (
                  <div>
                    <div className='header'>
                      <H1>Why archive {leadUser.name}?</H1>
                    </div>
                    <Form className='leadForm'>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={formField.id}
                        />
                      ))}
                      {archiveLeadApiState.error && (
                        <div style={{ color: 'red' }}>
                          {archiveLeadApiState.error.detail || archiveLeadApiState.error.message}
                        </div>
                      )}
                      <div className='bottomActions'>
                        <ButtonDeprecated
                          theme={BUTTON_THEME_RED}
                          onClick={formikBag.handleSubmit}
                          disabled={!formikBag.isValid}
                        >
                          {archiveLeadApiState.inProgress ? 'archiving' : 'archive'}
                        </ButtonDeprecated>
                      </div>
                    </Form>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

ArchiveLeadPage.displayName = 'ArchiveLeadPage';

export default ArchiveLeadPage;
